'use client';

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  Alert,
  Box,
  Button,
  Container,
  PasswordInput,
  Paper,
  Stack,
  SimpleGrid,
  Text,
  TextInput,
  Title,
} from '@mantine/core';

import { getSupabaseBrowserClientSafe } from '../../../lib/supabase/client';
import { MantineNextImage } from '../../../components/ui/MantineNextImage';
import { STORAGE_ASSET_URLS } from '../../../lib/content/storageAssets';

const adminLoginVisualImage = STORAGE_ASSET_URLS.familyImage8;

export default function AdminLogin() {
  const searchParams = useSearchParams();
  const unauthorized = useMemo(() => searchParams.get('unauthorized') === '1', [searchParams]);
  const expired = useMemo(() => searchParams.get('expired') === '1', [searchParams]);
  const redirect = useMemo(() => searchParams.get('redirect') || '/admin', [searchParams]);
  const routeNotice = useMemo(
    () =>
      unauthorized
        ? 'Pääsy on rajoitettu. Ota yhteyttä järjestelmänvalvojaan, jos tämän pitäisi mielestäsi toimia.'
        : expired
          ? 'Istuntosi on vanhentunut. Kirjaudu uudelleen.'
          : null,
    [unauthorized, expired],
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(routeNotice);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);
  const isCooldown = cooldownSeconds > 0;

  useEffect(() => {
    if (unauthorized || expired) {
      const client = getSupabaseBrowserClientSafe();
      client?.auth.signOut();
    }
  }, [unauthorized, expired]);

  useEffect(() => {
    if (!isCooldown) return;
    const timer = window.setInterval(() => {
      setCooldownSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isCooldown]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isCooldown) {
      setStatus('error');
      setMessage(`Odota ${cooldownSeconds} s ennen uuden kirjautumislinkin pyytämistä.`);
      return;
    }
    setStatus('loading');
    setMessage(null);
    setPasswordError(null);

    try {
      const response = await fetch('/api/admin/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          redirect,
        }),
      });
      const payload = (await response.json().catch(() => null)) as
        | { error?: string; code?: string; directSignInUrl?: string }
        | null;

      if (!response.ok) {
        if (payload?.code === 'INVALID_PASSWORD' || response.status === 401) {
          setStatus('error');
          setPasswordError(payload?.error || 'Virheellinen admin-salasana.');
          setMessage(null);
          return;
        }
        if (response.status === 429 || (payload?.error || '').toLowerCase().includes('rate limit')) {
          setCooldownSeconds(60);
          setStatus('error');
          setMessage('Sähköpostin lähetys on rajoitettu. Odota 60 s ja yritä uudelleen.');
          return;
        }
        setStatus('error');
        setMessage(payload?.error || 'Kirjautuminen ei ole juuri nyt käytettävissä. Yritä hetken päästä uudelleen.');
        return;
      }

      if (payload?.directSignInUrl) {
        setStatus('loading');
        setMessage('Kirjataan sisään...');
        window.location.assign(payload.directSignInUrl);
        return;
      }

      setCooldownSeconds(60);
      setStatus('sent');
      setMessage('Tarkista sähköpostisi kirjautumislinkkiä varten.');
    } catch (err) {
      setStatus('error');
      setPasswordError(null);
      setMessage(err instanceof Error ? err.message : 'Kirjautuminen epäonnistui.');
    }
  };

  return (
    <Box
      mih="100dvh"
      py={{ base: 12, md: 24 }}
        style={{
          background:
          'radial-gradient(circle at top left, rgba(22, 168, 224, 0.1), transparent 26%), linear-gradient(180deg, #f3f7fc 0%, #f7f9fd 55%, #edf3fb 100%)',
        }}
    >
      <Container size="xl">
        <Paper
          p={{ base: 18, md: 34 }}
          radius={28}
          withBorder
          style={{
            borderColor: 'var(--admin-border)',
            backgroundColor: 'rgba(255, 253, 248, 0.96)',
            boxShadow: 'var(--admin-shadow-strong)',
          }}
        >
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={{ base: 20, md: 30 }}>
            <Stack gap={28} justify="space-center" py={{ md: 10 }}>
              <div>
                <Title
                  order={1}
                  style={{
                    color: 'var(--admin-text)',
                    fontSize: 'clamp(1.75rem, 3vw, 2.75rem)',
                    fontWeight: 650,
                    lineHeight: 1.02,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Kirjaudu sisään
                </Title>
              </div>

              <form onSubmit={handleSubmit}>
                <Stack gap={14}>
                  <TextInput
                    type="email"
                    label="Sähköposti"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    placeholder="name@vakuutuslaskuri.fi"
                    error={status === 'error' && !passwordError ? message || 'Kirjautuminen epäonnistui' : undefined}
                    required
                    autoComplete="email"
                    size="lg"
                    variant="filled"
                    styles={{
                      label: { color: 'var(--admin-text)', fontSize: 16, fontWeight: 600, marginBottom: 8 },
                      input: {
                        borderRadius: 999,
                        borderColor: 'var(--admin-border)',
                        backgroundColor: 'var(--admin-panel)',
                        minHeight: 58,
                        paddingInline: 22,
                        fontSize: 17,
                      },
                    }}
                  />

                  <PasswordInput
                    label="Admin-salasana"
                    description={passwordError || undefined}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.currentTarget.value);
                      if (passwordError) setPasswordError(null);
                    }}
                    placeholder="Syötä salasana"
                    required
                    autoComplete="current-password"
                    size="lg"
                    variant="filled"
                    styles={{
                      label: { color: 'var(--admin-text)', fontSize: 16, fontWeight: 600, marginBottom: 8 },
                      description: {
                        color: passwordError ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-gray-6)',
                      },
                      input: {
                        borderRadius: 999,
                        borderColor: 'var(--admin-border)',
                        backgroundColor: 'var(--admin-panel)',
                        minHeight: 58,
                        paddingInline: 22,
                        fontSize: 17,
                      },
                    }}
                  />

                  {message && status !== 'error' ? (
                    <Alert color={status === 'sent' ? 'green' : 'orange'} variant="light" radius="md">
                      {message}
                    </Alert>
                  ) : null}

                  <Button
                    type="submit"
                    size="lg"
                    fullWidth
                    disabled={status === 'loading' || isCooldown}
                    styles={{
                      root: {
                        borderRadius: 999,
                        backgroundColor: 'var(--admin-accent, #264b96)',
                        border: '1px solid transparent',
                        color: 'var(--mantine-color-white)',
                        marginTop: 6,
                        minHeight: 58,
                        boxShadow: 'var(--admin-shadow-soft)',
                      },
                      label: {
                        color: 'var(--mantine-color-white)',
                        fontWeight: 700,
                      },
                    }}
                  >
                    {status === 'loading'
                      ? 'Kirjaudutaan...'
                      : isCooldown
                        ? `Yritä uudelleen ${cooldownSeconds} s kuluttua`
                        : 'Kirjaudu'}
                  </Button>
                </Stack>
              </form>

              <Text size="sm" c="var(--admin-text-subtle)">
                © 2026 Vakuutuslaskuri. Powered by Little Buck Oy.
              </Text>
            </Stack>

            <Paper
              radius={36}
              p={0}
              style={{
                minHeight: 720,
                overflow: 'hidden',
                position: 'relative',
                border: '1px solid var(--admin-border)',
                boxShadow: 'var(--admin-shadow-soft)',
              }}
            >
              <MantineNextImage
                src={adminLoginVisualImage}
                alt="Perhe kotona"
                width={1400}
                height={1800}
                sizes="(max-width: 1200px) 100vw, 50vw"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
              <Box
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(180deg, rgba(8, 26, 62, 0.36) 0%, rgba(8, 26, 62, 0.58) 100%)',
                }}
              />
              <Box
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'radial-gradient(circle at 20% 18%, rgba(255, 255, 255, 0.34), transparent 44%), radial-gradient(circle at 78% 82%, rgba(145, 198, 255, 0.32), transparent 46%)',
                }}
              />

              <Stack
                align="center"
                justify="center"
                h="100%"
                style={{
                  position: 'relative',
                  zIndex: 1,
                  textAlign: 'center',
                  color: '#fff',
                  padding: '2.5rem',
                }}
              >
                <Title order={2} style={{ fontSize: 'clamp(2.3rem, 4.8vw, 4.3rem)', fontWeight: 600, color: 'white' }}>
                  Vakuutuslaskuri
                </Title>
                <Text size="xl" maw={440} style={{ color: 'rgba(255, 255, 255, 0.92)' }}>
                  Riippumaton vakuutusvertailualusta fiksumpiin asiakasvalintoihin.
                </Text>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Paper>
      </Container>
    </Box>
  );
}
